import { ID, IDPrefixes, TaskLinkFields } from '@griply/types';
import { createPrefixId, taskGoalIds, taskLifeAreaIds } from '@griply/utils';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { getTasksQuery } from '../queries/get-tasks-query';
import {
  FirestoreInstance,
  OrderByDirection,
  WhereFilterOp,
} from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type DeleteGoalsOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TQueryRef = unknown,
  TConstraint = unknown,
  TDb = FirestoreInstance,
> = {
  goalIds: ID[];
  uid: ID;
  db: TDb;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: ID) => TDocRef;
  deleteDoc: (ref: NoInfer<TDocRef>) => Promise<void> | void;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  query: (
    ref: NoInfer<TCollectionRef>,
    ...constraints: TConstraint[]
  ) => TQueryRef;
  where: (field: string, op: WhereFilterOp, value: unknown) => TConstraint;
  orderBy: (field: string, direction?: OrderByDirection) => TConstraint;
  // The query carries no converter, so `data()` is the stored document — only the link
  // fields are read here, and they are read defensively.
  getDocs: (query: NoInfer<TQueryRef>) => Promise<{
    docs: ReadonlyArray<{ ref: TDocRef; data: () => TaskLinkFields }>;
  }>;
  serverTimestamp: () => unknown;
  writeBatch?: WriteBatchFn<NoInfer<TDocRef>, NoInfer<TDb>>;
  onWriteError: (error: unknown) => void;
};

export type GoalDeletionTaskDoc<TDocRef> = {
  ref: TDocRef;
  data: () => TaskLinkFields;
};

export type GoalDeletionPartition<TDocRef> = {
  taskUpdates: { ref: TDocRef; data: Record<string, unknown> }[];
  taskRefs: TDocRef[];
};

// Three outcomes per task: untouched, unlinked from the deleted goal, or deleted with it.
export const partitionTasksForGoalDeletion = <TDocRef>(
  docs: ReadonlyArray<GoalDeletionTaskDoc<TDocRef>>,
  goalIds: ID[],
  serverTimestamp: () => unknown,
): GoalDeletionPartition<TDocRef> => {
  const removedGoalIds = new Set(goalIds);
  const taskUpdates: GoalDeletionPartition<TDocRef>['taskUpdates'] = [];
  const taskRefs: TDocRef[] = [];

  docs.forEach(({ ref, data }) => {
    const task = data();
    const currentGoalIds = taskGoalIds(task);

    if (!currentGoalIds.some((goalId) => removedGoalIds.has(goalId))) {
      return;
    }

    const goalIdsLeft = currentGoalIds.filter(
      (goalId) => !removedGoalIds.has(goalId),
    );
    const lifeAreaIdsLeft = taskLifeAreaIds(task);

    // A task still linked to something else outlives the goal; only the link goes.
    if (goalIdsLeft.length || lifeAreaIdsLeft.length) {
      taskUpdates.push({
        ref,
        data: {
          goalIds: goalIdsLeft,
          goalId: goalIdsLeft[0] ?? null,
          // Subscribers keep the previously converted object when `updatedAt` is unchanged,
          // so without this the unlink stays invisible until something else touches the task.
          updatedAt: serverTimestamp(),
        },
      });
      return;
    }

    taskRefs.push(ref);
  });

  return { taskUpdates, taskRefs };
};

export const deleteGoals = async <
  TDocRef,
  TCollectionRef,
  TQueryRef,
  TConstraint,
  TDb = FirestoreInstance,
>(
  options: DeleteGoalsOptions<
    TDocRef,
    TCollectionRef,
    TQueryRef,
    TConstraint,
    TDb
  >,
): Promise<void> => {
  const {
    goalIds,
    uid,
    db,
    collection,
    doc,
    deleteDoc,
    updateDoc,
    query,
    where,
    orderBy,
    getDocs,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  if (!goalIds.length) {
    return;
  }

  const goalCollection = collection(db, FirestoreCollections.Goals);
  const relationshipCollection = collection(
    db,
    FirestoreCollections.Relationships,
  );

  // One broad read: the query can only narrow on the legacy scalar, which would miss every
  // task whose link to a deleted goal is not its first one.
  const snapshot = await getDocs(
    getTasksQuery<TCollectionRef, TQueryRef, TConstraint, TDb>({
      db,
      collection,
      query,
      where,
      orderBy,
      uid,
      showCompleted: true,
    }),
  );

  const { taskUpdates, taskRefs } = partitionTasksForGoalDeletion(
    snapshot.docs,
    goalIds,
    serverTimestamp,
  );

  const goalRefs = goalIds.map((id) => doc(goalCollection, id));
  const goalRelationshipRefs = goalIds.map((id) =>
    doc(relationshipCollection, createPrefixId(id, IDPrefixes.Goal)),
  );

  if (!writeBatch) {
    taskUpdates.forEach(({ ref, data }) =>
      detachWrite(updateDoc(ref, data), onWriteError),
    );
    taskRefs.forEach((ref) => detachWrite(deleteDoc(ref), onWriteError));
    goalRefs.forEach((ref) => detachWrite(deleteDoc(ref), onWriteError));
    goalRelationshipRefs.forEach((ref) =>
      detachWrite(deleteDoc(ref), onWriteError),
    );
    return;
  }

  const batch = new BatchDocuments(db, writeBatch);
  taskUpdates.forEach(({ ref, data }) => batch.updateDocument({ ref, data }));
  taskRefs.forEach((ref) => batch.deleteDocument(ref));
  goalRefs.forEach((ref) => batch.deleteDocument(ref));
  goalRelationshipRefs.forEach((ref) => batch.deleteDocument(ref));
  detachWrite(batch.commit(), onWriteError);
};
